import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Loader2, Sparkles, Camera } from "lucide-react";
import imageCompression from 'browser-image-compression';
import { CameraCapture } from "@/components/camera/CameraCapture";
import { CaptureResult } from "@/types/camera";

interface CreateStoryProps {
  onClose: () => void;
}

export const CreateStory = ({ onClose }: CreateStoryProps) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);

  const createStoryMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Nenhum arquivo selecionado');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let fileToUpload = file;

      // Compress image if needed
      if (file.type.startsWith('image/')) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg'
        };
        fileToUpload = await imageCompression(file, options);
      }

      // Upload do arquivo
      const fileExt = fileToUpload.type.startsWith('video/') ? 'mp4' : 'jpg';
      const fileName = `${user.id}/story-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('stories')
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('stories')
        .getPublicUrl(fileName);

      // Criar registro do story
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
      
      const { error: insertError } = await supabase
        .from('stories')
        .insert({
          user_id: user.id,
          media_type: mediaType,
          media_url: publicUrl,
          caption: caption || null,
          duration_seconds: mediaType === 'video' ? 15 : 5
        });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      toast.success("Story publicado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ['stories-carousel'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao publicar story");
    }
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validar tipo
    if (!selectedFile.type.startsWith('image/') && !selectedFile.type.startsWith('video/')) {
      toast.error('Apenas imagens e vídeos são permitidos');
      return;
    }

    // Validar tamanho (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 10MB');
      return;
    }

    setFile(selectedFile);
    
    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleCameraCapture = (result: CaptureResult) => {
    setCameraOpen(false);
    
    // Converter blob para File
    const capturedFile = new File(
      [result.blob], 
      `story-${Date.now()}.${result.type === 'photo' ? 'jpg' : 'webm'}`,
      { type: result.blob.type }
    );
    
    setFile(capturedFile);
    setPreview(result.dataUrl);
  };

  const handleSubmit = () => {
    if (!file) {
      toast.error('Selecione uma foto ou vídeo');
      return;
    }
    createStoryMutation.mutate();
  };

  if (cameraOpen) {
    return (
      <CameraCapture
        onCapture={handleCameraCapture}
        onClose={() => setCameraOpen(false)}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Criar Story
        </h2>
        <p className="text-sm text-muted-foreground">
          Capture um momento ou escolha uma foto/vídeo da galeria
        </p>
      </div>

      {/* File Upload */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {preview ? (
          <div className="relative aspect-[9/16] max-h-[450px] rounded-2xl overflow-hidden bg-gradient-to-br from-muted/50 to-muted shadow-lg">
            {file?.type.startsWith('video/') ? (
              <video src={preview} className="w-full h-full object-cover" controls />
            ) : (
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-3 right-3 backdrop-blur-xl bg-background/80 hover:bg-background/90 shadow-lg"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-2" />
              Trocar
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Button
              variant="default"
              className="w-full h-32 rounded-2xl bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all"
              onClick={() => setCameraOpen(true)}
            >
              <div className="flex flex-col items-center gap-2">
                <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
                  <Camera className="w-8 h-8 text-white" />
                </div>
                <span className="text-base font-semibold text-white">Abrir Câmera</span>
                <p className="text-xs text-white/80">Tire uma foto ou grave um vídeo</p>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full h-24 border-2 border-dashed border-border/50 hover:border-primary/50 rounded-2xl bg-gradient-to-br from-muted/30 to-transparent hover:from-muted/50 transition-all"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-6 h-6 text-muted-foreground" />
                <div className="text-center">
                  <span className="text-sm font-medium">Escolher da Galeria</span>
                  <p className="text-xs text-muted-foreground">Máximo 10MB</p>
                </div>
              </div>
            </Button>
          </div>
        )}
      </div>

      {/* Caption */}
      <div>
        <Label>Legenda (opcional)</Label>
        <Input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Adicione uma legenda..."
          maxLength={200}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {caption.length}/200 caracteres
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!file || createStoryMutation.isPending}
          className="flex-1"
        >
          {createStoryMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Publicando...
            </>
          ) : (
            'Publicar'
          )}
        </Button>
      </div>
    </div>
  );
};